/**
 * API helpers for the Career Advisor chat.
 * All requests are JSON POST with CSRF token.
 */

const REQUEST_TIMEOUT_MS = 60000

export function getCSRFToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute("content") : ""
}

function headers() {
  return {
    "Content-Type": "application/json",
    "X-CSRF-Token": getCSRFToken(),
    Accept: "application/json"
  }
}

async function fetchWithTimeout(url, options) {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  try {
    const response = await fetch(url, { ...options, signal: controller.signal })
    return parseResponse(response)
  } finally {
    clearTimeout(timeout)
  }
}

async function parseResponse(response) {
  if (!response.ok) {
    const body = await response.text()
    try {
      return JSON.parse(body)
    } catch {
      throw new Error(`Server error (${response.status})`)
    }
  }
  return response.json()
}

/**
 * Create a new advisor session.
 * @param {string} url - The create session endpoint
 * @param {Object} entryContext - Pre-filled context from calculator/SalaryDB
 * @param {string} [sessionType] - Session type
 * @param {string} [persona] - Advisor persona (coach, analyst, strategist, mentor)
 * @returns {Promise<Object>} Session data with greeting HTML
 */
export async function createSession(url, entryContext = {}, sessionType = "career_checkin", persona = "coach") {
  return fetchWithTimeout(url, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ entry_context: entryContext, session_type: sessionType, persona })
  })
}

/**
 * Send a message to an existing advisor session.
 * @param {string} url - The message endpoint (with session ID)
 * @param {string} message - The user's message text
 * @returns {Promise<Object>} Response with HTML and metadata
 */
export async function sendMessage(url, message) {
  return fetchWithTimeout(url, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ message })
  })
}

/**
 * Send a message via SSE streaming.
 * Uses fetch + ReadableStream (not EventSource, since POST is required).
 *
 * @param {string} url - The stream endpoint URL (with :id placeholder)
 * @param {string} sessionId - The session ID
 * @param {string} content - The user's message text
 * @param {Object} callbacks - Event handlers
 * @param {Function} callbacks.onTextDelta - Called with each text chunk
 * @param {Function} callbacks.onToolResults - Called with tool result data
 * @param {Function} callbacks.onStatus - Called with status message updates
 * @param {Function} callbacks.onStreamEnd - Called with final response data
 * @param {Function} callbacks.onBlocked - Called when input is blocked
 * @param {Function} callbacks.onError - Called on error
 * @returns {Promise<void>}
 */
export async function sendMessageStreaming(url, sessionId, content, callbacks) {
  const streamUrl = url.replace(":id", sessionId)
  const response = await fetch(streamUrl, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ content })
  })

  if (!response.ok) {
    const errorText = await response.text()
    const parsed = safeJsonParse(errorText)
    if (callbacks.onError) callbacks.onError(parsed || { message: `Server error (${response.status})` })
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    // Parse complete SSE events from buffer
    const events = parseSSEBuffer(buffer)
    buffer = events.remainder

    for (const event of events.parsed) {
      dispatchSSEEvent(event, callbacks)
    }
  }

  // Process any remaining buffer
  if (buffer.trim()) {
    const events = parseSSEBuffer(buffer + "\n\n")
    for (const event of events.parsed) {
      dispatchSSEEvent(event, callbacks)
    }
  }
}

/**
 * Parse SSE events from a text buffer.
 * Returns parsed events and the remaining unparsed text.
 */
export function parseSSEBuffer(buffer) {
  const parsed = []
  const parts = buffer.split("\n\n")
  const remainder = parts.pop() || ""

  for (const part of parts) {
    if (!part.trim()) continue

    let eventType = "message"
    let data = ""

    for (const line of part.split("\n")) {
      if (line.startsWith("event: ")) {
        eventType = line.slice(7).trim()
      } else if (line.startsWith("data: ")) {
        data = line.slice(6)
      }
    }

    if (data) {
      const parsedData = safeJsonParse(data)
      if (parsedData) {
        parsed.push({ type: eventType, data: parsedData })
      }
    }
  }

  return { parsed, remainder }
}

function dispatchSSEEvent(event, callbacks) {
  switch (event.type) {
    case "text_delta":
      if (callbacks.onTextDelta) callbacks.onTextDelta(event.data)
      break
    case "tool_results":
      if (callbacks.onToolResults) callbacks.onToolResults(event.data)
      break
    case "status":
      if (callbacks.onStatus) callbacks.onStatus(event.data)
      break
    case "stream_end":
      if (callbacks.onStreamEnd) callbacks.onStreamEnd(event.data)
      break
    case "blocked":
      if (callbacks.onBlocked) callbacks.onBlocked(event.data)
      break
    case "error":
      if (callbacks.onError) callbacks.onError(event.data)
      break
  }
}

function safeJsonParse(str) {
  try {
    return JSON.parse(str)
  } catch {
    return null
  }
}
